import { useMemo } from 'react';
import { useProducts } from '../shared/hooks/useProducts';
import { PurchaseOrderItem } from '../shared/types/inventory';
import { ShoppingCart, Trash2, AlertCircle, Package, DollarSign, TrendingUp, Loader2, RefreshCw } from 'lucide-react';
import { Badge } from '../shared/components/Badge';
import { getStatusInfo } from '../shared/utils/inventoryCalculations';
import { PageTitle} from '../../../../_metronic/layout/core'

export function PurchaseOrderPage() {
  const { products, loading, error, refetch } = useProducts();

  const purchaseOrderItems = useMemo<PurchaseOrderItem[]>(() => {
    return products
      .filter(product => product.pedidoSugerido > 0)
      .map(product => {
        const quantidade = Math.ceil(product.pedidoSugerido);
        const precoUnitario = product.precoUnitario || 0;
        const subtotal = quantidade * precoUnitario;

        let prioridade: 'alta' | 'media' | 'baixa' = 'baixa';
        if (product.status === 'ruptura' || product.status === 'iminente') {
          prioridade = 'alta';
        } else if (product.status === 'minimo' || product.status === 'critica') {
          prioridade = 'media';
        }

        return {
          product,
          quantidade,
          precoUnitario,
          subtotal,
          fornecedor: product.fornecedor || 'Fornecedor nao informado',
          prioridade
        };
      })
      .sort((a, b) => {
        const prioridadeOrder = { alta: 0, media: 1, baixa: 2 };
        return prioridadeOrder[a.prioridade] - prioridadeOrder[b.prioridade];
      });
  }, [products]);

  const totals = useMemo(() => {
    const total = purchaseOrderItems.reduce((sum, item) => sum + item.subtotal, 0);
    const totalItens = purchaseOrderItems.length;
    const totalUnidades = purchaseOrderItems.reduce((sum, item) => sum + item.quantidade, 0);
    const alta = purchaseOrderItems.filter(i => i.prioridade === 'alta').length;
    const media = purchaseOrderItems.filter(i => i.prioridade === 'media').length;

    return { total, totalItens, totalUnidades, alta, media };
  }, [purchaseOrderItems]);

  const getPrioridadeBadge = (prioridade: 'alta' | 'media' | 'baixa') => {
    const configs = {
      alta: { label: 'Alta Prioridade', color: 'text-red-900', bgColor: 'bg-red-100' },
      media: { label: 'Media Prioridade', color: 'text-yellow-900', bgColor: 'bg-yellow-100' },
      baixa: { label: 'Baixa Prioridade', color: 'text-gray-900', bgColor: 'bg-gray-100' }
    };
    const config = configs[prioridade];
    return <Badge label={config.label} color={config.color} bgColor={config.bgColor} />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
          <p className="text-gray-500 text-sm">Carregando pedido sugerido...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4 max-w-md text-center">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>
          <p className="text-gray-900 font-semibold">Erro ao carregar dados</p>
          <p className="text-gray-500 text-sm">{error}</p>
          <button
            onClick={refetch}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
          >
            <RefreshCw className="w-4 h-4" />
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
        <PageTitle>Pedido de Compra Sugerido</PageTitle>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Pedido de Compra Sugerido</h1>
          <p className="text-gray-600 mt-2">Carrinho automático baseado nas necessidades de reposição</p>
        </div>
        <button
          onClick={refetch}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700 shadow-sm"
        >
          <RefreshCw className="w-4 h-4" />
          Atualizar
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total do Pedido</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                R$ {totals.total.toFixed(2)}
              </p>
            </div>
            <div className="p-3 rounded-full bg-green-100">
              <DollarSign className="w-6 h-6 text-green-700" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Itens Diferentes</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{totals.totalItens}</p>
            </div>
            <div className="p-3 rounded-full bg-blue-100">
              <Package className="w-6 h-6 text-blue-700" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Unidades</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{totals.totalUnidades}</p>
            </div>
            <div className="p-3 rounded-full bg-teal-100">
              <TrendingUp className="w-6 h-6 text-teal-700" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Urgentes</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{totals.alta}</p>
              <p className="text-xs text-gray-500 mt-1">{totals.media} médias</p>
            </div>
            <div className="p-3 rounded-full bg-red-100">
              <AlertCircle className="w-6 h-6 text-red-700" />
            </div>
          </div>
        </div>
      </div>

      {purchaseOrderItems.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 border border-gray-200 text-center">
          <ShoppingCart className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Nenhum pedido necessário</h3>
          <p className="text-gray-600">Todos os produtos estão com estoque adequado no momento.</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden mb-6">
            <div className="p-6 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <ShoppingCart className="w-5 h-5 text-gray-700" />
                  <h2 className="text-xl font-bold text-gray-900">Itens do Pedido</h2>
                </div>
                {/*<button disabled className="flex items-center space-x-2 px-4 py-2 bg-light-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium">
                  <Package className="w-4 h-4" />
                  <span>Finalizar Pedido</span>
                </button>*/}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Produto
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Status Atual
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Quantidade
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Prioridade
                    </th>
                    {/*<th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Ações
                    </th>*/}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {purchaseOrderItems.map((item) => {
                    const statusInfo = getStatusInfo(item.product.status);
                    return (
                      <tr key={item.product.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{item.product.nome}</p>
                            <p className="text-xs text-gray-500">{item.product.ean}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <Badge
                            label={statusInfo.label}
                            color={statusInfo.color}
                            bgColor={statusInfo.bgColor}
                          />
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-gray-900 text-right">
                          {item.quantidade} un
                        </td>
                        <td className="px-6 py-4 text-center">
                          {getPrioridadeBadge(item.prioridade)}
                        </td>
                        {/*<td className="px-6 py-4 text-center">
                          <button className="text-red-600 hover:text-red-800 transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>*/}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
